import React, { useState } from "react";
import { slide as Menu } from "react-burger-menu";
import { Link, useLocation } from "react-router-dom";
import {
  Home,
  Heart,
  Calculator,
  Newspaper,
  MessageCircle,
  ChevronLeft,
  UserPlus,
  LogIn,
  Gift,
} from "lucide-react";

function SideBar() {
  const location = useLocation();
  const [isOpen, setOpen] = useState(false);

  const handleStateChange = (state) => setOpen(state.isOpen);
  const closeMenu = () => setOpen(false);
  const isActive = (path) => location.pathname === path;

  return (
    <div className="relative">
      <Menu
        right
        width={"300px"}
        isOpen={isOpen}
        onStateChange={handleStateChange}
        styles={burgerStyles}
        customBurgerIcon={
          <div className="flex flex-col justify-center gap-1.5 p-1">
            <span className="w-6 h-0.5 bg-gray-900 rounded-full"></span>
            <span className="w-4 h-0.5 bg-gray-900 rounded-full ml-auto"></span>
            <span className="w-6 h-0.5 bg-gray-900 rounded-full"></span>
          </div>
        }
      >
        <div className="flex flex-col h-full text-right outline-none">
          {/* Header Area */}
          <div className="mb-6 flex items-center justify-between border-b border-white/5 pb-6">
            <div>
              <h2 className="text-xl font-bold text-white tracking-tight">
                الخير
              </h2>
              <p className="text-[10px] text-gray-500 uppercase tracking-widest mt-1">
                القائمة الملاحية
              </p>
            </div>
          </div>

          {/* Quick Action Buttons - Crucial for Mobile UX */}
          <div className="grid grid-cols-2 gap-2 mb-8">
            <Link
              onClick={closeMenu}
              to="/register"
              className="flex items-center justify-center gap-2 py-3 bg-blue-600 rounded-xl text-white text-xs font-bold shadow-lg shadow-blue-900/20"
            >
              <UserPlus size={14} /> تسجيل
            </Link>
            <Link
              onClick={closeMenu}
              to="/join"
              className="flex items-center justify-center gap-2 py-3 bg-white/10 rounded-xl text-white text-xs font-bold hover:bg-white/20"
            >
              <LogIn size={14} /> دخول
            </Link>
          </div>

          {/* Main Navigation Scrollable Area */}
          <div className="flex flex-col gap-1 overflow-y-auto custom-scrollbar pr-1">
            <SidebarLink
              to="/"
              icon={<Home size={18} />}
              label="الرئيسية"
              active={isActive("/")}
              onClick={closeMenu}
            />
            <SidebarLink
              to="/kofalat"
              icon={<Heart size={18} />}
              label="كفالات"
              active={isActive("/kofalat")}
              onClick={closeMenu}
            />
            <SidebarLink
              to="/zaka-calculator"
              icon={<Calculator size={18} />}
              label="حاسبة الزكاة"
              active={isActive("/zaka-calculator")}
              onClick={closeMenu}
            />
            <SidebarLink
              to="/gift"
              icon={<Gift size={18} />}
              label="تقديم هدية"
              active={isActive("/gift")}
              onClick={closeMenu}
            />
            <SidebarLink
              to="/media-releases"
              icon={<Newspaper size={18} />}
              label="حملاتنا"
              active={isActive("/media-releases")}
              onClick={closeMenu}
            />

            <div className="my-4 border-t border-white/5 w-10 mr-auto" />

            <h3 className="text-yellow-500/80 text-[10px] font-bold uppercase tracking-[2px] mb-2 px-3">
              التواصل والمعلومات
            </h3>
            <SidebarLink
              to="/about-us"
              label="من نحن"
              active={isActive("/about-us")}
              onClick={closeMenu}
            />
            <SidebarLink
              to="/call-us"
              icon={<MessageCircle size={18} />}
              label="إتصل بنا"
              active={isActive("/call-us")}
              onClick={closeMenu}
            />
            <SidebarLink
              to="/blog"
              label="المدونة"
              active={isActive("/blog")}
              onClick={closeMenu}
            />
          </div>

          {/* Sticky Footer */}
          <div className="mt-auto pt-6 border-t border-white/5">
            <button className="w-full py-4 bg-yellow-400 text-black font-bold rounded-2xl hover:bg-yellow-500 transition-colors mb-4">
              تبرع الآن
            </button>
            <div className="text-center text-gray-600 text-[10px] pb-4">
              جميع الحقوق محفوظة © 2026
            </div>
          </div>
        </div>
      </Menu>
    </div>
  );
}

function SidebarLink({ to, label, icon, active, onClick }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`group flex items-center justify-between p-3.5 rounded-xl transition-all duration-300 outline-none mb-1
        ${active ? "bg-white/10 text-white shadow-inner" : "text-gray-400 hover:text-white hover:bg-white/5"}`}
    >
      <div className="flex items-center gap-3">
        {icon && (
          <span
            className={`${active ? "text-blue-400" : "text-gray-500 group-hover:text-blue-400"} transition-colors`}
          >
            {icon}
          </span>
        )}
        <span className="text-sm font-medium">{label}</span>
      </div>
      <ChevronLeft
        size={14}
        className={`transition-all duration-300 ${active ? "opacity-100 translate-x-0" : "opacity-0 translate-x-2 group-hover:opacity-50 group-hover:translate-x-0"}`}
      />
    </Link>
  );
}

const burgerStyles = {
  bmBurgerButton: {
    position: "relative", // Changed to relative so it fits in your Navbar's flexbox
    width: "24px",
    height: "24px",
  },
  bmMenuWrap: {
    position: "fixed",
    height: "100%",
    top: "0",
    transition: "all 0.4s cubic-bezier(0.7, 0, 0.3, 1)",
  },
  bmMenu: {
    background: "#0f172a", // Darker slate for premium feel
    padding: "1.5rem 1.2rem",
    boxShadow: "-10px 0 30px rgba(0,0,0,0.5)",
  },
  bmItemList: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  bmOverlay: {
    background: "rgba(15, 23, 42, 0.7)",
    backdropFilter: "blur(6px)",
    top: "0",
    left: "0",
  },
  bmCrossButton: {
    height: "32px",
    width: "32px",
    left: "20px",
    top: "20px",
  },
  bmCross: {
    background: "#475569",
    height: "20px",
  },
};

export default SideBar;
